import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { getOperacional } from "./api"
import { rangoDelMes } from "./helpers"
import HeaderFiltros from "./HeaderFiltros"
import ResumenTab from "./tabs/ResumenTab"
import SucursalesTab from "./tabs/SucursalesTab"
import MetodosTab from "./tabs/MetodosTab"
import FacturasTab from "./tabs/FacturasTab"
import AlertasTab from "./tabs/AlertasTab"
import DesgloseTab from "./tabs/DesgloseTab"
import DatafonoTab from "./tabs/DatafonoTab"
import ConciliacionTab from "./tabs/ConciliacionTab"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Comprobantes", link: "#" },
  { title: "Medios de Pago", link: "#" },
]

const TABS = [
  { id: "resumen", label: "Resumen", icon: "mdi-view-dashboard-outline" },
  { id: "sucursales", label: "Sucursales", icon: "mdi-store" },
  { id: "metodos", label: "Métodos", icon: "mdi-credit-card-multiple" },
  { id: "facturas", label: "Facturas", icon: "mdi-receipt-text-outline" },
  { id: "alertas", label: "Alertas", icon: "mdi-alert-octagon-outline" },
  { id: "desglose", label: "Desglose", icon: "mdi-table-pivot", nuevo: true, sep: true },
  { id: "datafono", label: "Datáfono", icon: "mdi-credit-card-check-outline", nuevo: true, grupo: "datafono" },
  { id: "conciliacion", label: "Conciliación", icon: "mdi-scale-balance", nuevo: true, grupo: "datafono" },
]

const ahora = new Date()

const MediosPago = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")
  const [sucursal, setSucursal] = useState("")
  const [tipoMetodo, setTipoMetodo] = useState("")
  const [campoFecha, setCampoFecha] = useState("fecha")

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  const [filtrosAplicados, setFiltrosAplicados] = useState(null)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Medios de Pago | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Medios de Pago"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setData(null)
    setError("")
    setFiltrosAplicados(null)
    setTabActivo("resumen")
    setSucursal("")
    setTipoMetodo("")
  }, [nitemisor])

  // Catálogos extraídos del response
  const sucursalesDisponibles = useMemo(() => {
    if (!data?.sucursal_metodos) return []
    return Array.from(new Set(data.sucursal_metodos.map(r => r.sucursal).filter(Boolean))).sort()
  }, [data])

  const metodosDisponibles = useMemo(() => {
    if (!data?.sucursal_metodos) return []
    return Array.from(new Set(data.sucursal_metodos.map(r => r.metodo_pago).filter(Boolean))).sort()
  }, [data])

  const cargar = useCallback(async opts => {
    setLoading(true)
    setError("")
    try {
      const response = await getOperacional(opts)
      const payload = response.data
      if (payload?.success === false) {
        throw new Error(payload?.message || "El servidor reportó un error.")
      }
      setData(payload?.data || payload)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar el análisis.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // En modo "mes" derivamos fecha_inicio/fecha_fin del mes/año para que los tabs
  // que consultan endpoints "facturas" (que sólo aceptan rango) no tengan que
  // re-calcularlo. En modo "rango" enviamos directamente las fechas elegidas.
  const handleConsultar = () => {
    const base = {
      nitemisor,
      sucursal: sucursal || undefined,
      tipo_metodo: tipoMetodo || undefined,
      campo_fecha: campoFecha,
      incluir: "todos",
    }
    let opts
    if (modo === "mes") {
      const { fecha_inicio, fecha_fin } = rangoDelMes(year, month)
      opts = { ...base, year, month, fecha_inicio, fecha_fin }
    } else {
      // El back exige year siempre — lo derivamos del año de fechaInicio.
      const yearRango = Number(String(fechaInicio).substring(0, 4)) || ahora.getFullYear()
      opts = { ...base, year: yearRango, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
    }
    setFiltrosAplicados(opts)
    cargar(opts)
  }

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  // Sin empresa
  if (!nitemisor) {
    return (
      <div
        style={{
          background: "#fff", borderRadius: 14, padding: 60,
          textAlign: "center", border: "1px dashed #cbd5e1",
        }}
      >
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha
          para iniciar el análisis.
        </p>
      </div>
    )
  }

  const alertasOperacionales = data?.alertas_operacionales || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Comprobantes
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Medios de Pago
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
            </div>
          </div>
          {data?.duration && (
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              <i className="mdi mdi-clock-outline me-1" />
              Procesado en {data.duration}
            </div>
          )}
        </div>

        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear}
          month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          sucursal={sucursal} setSucursal={setSucursal}
          tipoMetodo={tipoMetodo} setTipoMetodo={setTipoMetodo}
          campoFecha={campoFecha} setCampoFecha={setCampoFecha}
          sucursalesDisponibles={sucursalesDisponibles}
          metodosDisponibles={metodosDisponibles}
          loading={loading}
          onConsultar={handleConsultar}
          puedeConsultar={puedeConsultar}
        />
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
            color: "#991b1b", borderRadius: 10,
            display: "flex", alignItems: "center", gap: 10,
          }}
        >
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-auto"
            onClick={() => filtrosAplicados && cargar(filtrosAplicados)}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Loading inicial */}
      {!data && loading && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando análisis…</div>
        </div>
      )}

      {/* Empty state */}
      {!data && !loading && !error && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-credit-card-search-outline" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Selecciona el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Elige <strong>mes/año</strong> o <strong>rango de fechas</strong>, opcionalmente filtra por sucursal
            o tipo de método, y pulsa <strong>Consultar</strong> para ver los medios de pago de <strong>{empresaNombre}</strong>.
          </p>
        </div>
      )}

      {/* Tabs + contenido */}
      {data && (
        <>
          <div
            style={{
              background: "#fff", borderRadius: "14px 14px 0 0",
              border: "1px solid #e2e8f0", borderBottom: "none",
              padding: "0 12px",
              display: "flex", gap: 4, overflowX: "auto",
            }}
          >
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              const badgeAlertas = tab.id === "alertas" && alertasOperacionales.length > 0
              return (
                <React.Fragment key={tab.id}>
                  {tab.sep && (
                    <div
                      title="Analítica de pagos y datáfono"
                      style={{ alignSelf: "center", width: 1, height: 22, background: "#e2e8f0", margin: "0 6px" }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setTabActivo(tab.id)}
                    style={{
                      background: "none", border: "none",
                      padding: "14px 18px",
                      fontSize: 13, fontWeight: 600,
                      color: activo ? "#0f172a" : "#64748b",
                      borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                      cursor: "pointer", transition: "color 0.15s",
                      display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                    }}
                  >
                    <i className={`mdi ${tab.icon}`} style={tab.grupo === "datafono" ? { color: activo ? "#8b5cf6" : "#a78bdf" } : undefined} />
                    {tab.label}
                    {badgeAlertas && (
                      <span style={{
                        background: "#ef4444", color: "#fff",
                        padding: "1px 7px", borderRadius: 999,
                        fontSize: 10, fontWeight: 700,
                      }}>
                        {alertasOperacionales.length}
                      </span>
                    )}
                    {tab.nuevo && (
                      <span style={{
                        background: "#ede9fe", color: "#6d28d9",
                        padding: "1px 6px", borderRadius: 999,
                        fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3,
                      }}>
                        Nuevo
                      </span>
                    )}
                  </button>
                </React.Fragment>
              )
            })}
          </div>

          <div>
            {tabActivo === "resumen" && <ResumenTab data={data} alertasCount={alertasOperacionales.length} />}
            {tabActivo === "sucursales" && <SucursalesTab data={data} />}
            {tabActivo === "metodos" && <MetodosTab data={data} />}
            {tabActivo === "facturas" && <FacturasTab filtros={filtrosAplicados} />}
            {tabActivo === "alertas" && <AlertasTab alertasOperacionales={alertasOperacionales} filtros={filtrosAplicados} />}
            {tabActivo === "desglose" && <DesgloseTab filtros={filtrosAplicados} />}
            {tabActivo === "datafono" && <DatafonoTab filtros={filtrosAplicados} />}
            {tabActivo === "conciliacion" && <ConciliacionTab filtros={filtrosAplicados} />}
          </div>
        </>
      )}
    </div>
  )
}

MediosPago.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(MediosPago)
